import { LoadingOutlined } from '@ant-design/icons';
import { Col, Row, Select, Spin } from 'antd';
import { isEmpty } from 'underscore';
import useHomeStore from '../../../../../../store/homeStore';
import { DownloadType } from '../../../../../../types/metadata';
import { DownloadCheckboxKey } from '../../../../../../utils/common';
import priceFormatter from '../../../../../../utils/euroPriceFormatter';
import styles from '../../../Style/DatasetInfo.module.scss';
import {
  DatasetActionButtons,
  DownloadButton,
} from '../../DatasetActionButtons';

interface IPriceSectionProps {
  hasDatasets: boolean;
  price: number;
  isTnw: boolean;
  namePanel: string;
  disableDownload: boolean;
  isLocationAvailable: boolean;
  isDTU: boolean;
  timeStepInterval: string;
  setTimeStepInterval: (value: string) => void;
  datasetId: string;
  addedToCart: boolean;
  spectraAddedToCart: boolean;
  showDownloadButton: boolean;
  showCancelDownloadButtonParameters: boolean;
  showCancelDownloadButton2DSpectre: boolean;
  show2DSpectra: boolean;
  isAuthenticated: boolean;
  userId?: string;
  userGroupPermissions: Record<
    string,
    Record<string, { spectraAccess?: boolean }>
  >;
  handleAddToCart: () => void;
  handleAddSpectraToCart: () => void;
  handleRemoveFromCart: () => void;
  handleRemoveSpectraFromCart: () => void;
  handleStartDownloadProcess: (type: DownloadType) => void;
  handleCancelDownload: (type: DownloadType) => void;
  setShowSpectraModal: (value: boolean) => void;
  parameterCartLoading: boolean;
  spectraCartLoading: boolean;
  priceLoading: boolean;
  label?: string;
}

type TRenderActionButtonsParams = {
  showDownloadButton: boolean;
  showCancelButton: boolean;
  downloadType: DownloadType;
  addedToCart: boolean;
  cartLoading: boolean;
  removeFromCart: () => void;
  disabled?: boolean;
  authenticatedCheck?: boolean;
  selectedAction: string;
  setSelectedAction: (value: string) => void;
  addToCartSpectra?: () => void;
};

export const PriceSection = ({
  hasDatasets,
  price,
  isTnw,
  namePanel,
  disableDownload,
  isLocationAvailable,
  isDTU,
  timeStepInterval,
  setTimeStepInterval,
  datasetId,
  addedToCart,
  spectraAddedToCart,
  showDownloadButton,
  showCancelDownloadButtonParameters,
  showCancelDownloadButton2DSpectre,
  show2DSpectra,
  isAuthenticated,
  userId,
  userGroupPermissions,
  handleAddToCart,
  handleAddSpectraToCart,
  handleRemoveFromCart,
  handleRemoveSpectraFromCart,
  handleStartDownloadProcess,
  handleCancelDownload,
  setShowSpectraModal,
  parameterCartLoading,
  spectraCartLoading,
  label,
  priceLoading,
}: IPriceSectionProps) => {
  const priceSpectraSelection = useHomeStore(
    (state) => state.priceSpectraSelection
  );
  const setPriceSpectraSelection = useHomeStore(
    (state) => state.setPriceSpectraSelection
  );

  const priceParameterSelection = useHomeStore(
    (state) => state.priceParameterSelection
  );

  const setPriceParameterSelection = useHomeStore(
    (state) => state.setPriceParameterSelection
  );

  const isSpectraButtonDisabled = () => {
    if (!show2DSpectra) return true;

    if (isAuthenticated) {
      return (
        disableDownload ||
        (!userGroupPermissions[userId || '']?.[namePanel]?.spectraAccess &&
          !showDownloadButton)
      );
    }

    return false;
  };

  const renderActionButtons = ({
    showDownloadButton,
    showCancelButton,
    downloadType,
    addedToCart,
    cartLoading,
    removeFromCart,
    selectedAction,
    setSelectedAction,
    authenticatedCheck = false,
    disabled = false,
    addToCartSpectra,
  }: TRenderActionButtonsParams) => {
    const addToCartHandler = addToCartSpectra ?? handleAddToCart;

    const startProcess = () => {
      if (authenticatedCheck && !isAuthenticated) {
        setShowSpectraModal(true);
        return;
      }
      handleStartDownloadProcess(downloadType);
    };

    if (showDownloadButton) {
      switch (selectedAction) {
        case 'download':
          return (
            <DownloadButton
              id={`${label}_${namePanel}`}
              data-testid={`${label}_${namePanel}`}
              showCancelDownloadButton={showCancelButton}
              startDownloadProcess={startProcess}
              disableDownloadButtons={disableDownload || disabled}
              handleCancelDownload={() => handleCancelDownload(downloadType)}
              showDropdown={showDownloadButton}
              onSwitchToBuy={() => setSelectedAction('buy')}
            />
          );

        default:
          return (
            <DatasetActionButtons
              id={`${label}_${namePanel}`}
              data-testid={`${label}_${namePanel}`}
              addedToCart={addedToCart}
              addToCart={addToCartHandler}
              removeFromCart={removeFromCart}
              cartLoading={cartLoading}
              hasDatasets={hasDatasets}
              disabled={!isLocationAvailable || disabled}
              showDropdown={showDownloadButton}
              onSwitchToDownload={() => setSelectedAction('download')}
            />
          );
      }
    }

    return (
      <DatasetActionButtons
        id={`${label}_${namePanel}`}
        data-testid={`${label}_${namePanel}`}
        addedToCart={addedToCart}
        addToCart={addToCartHandler}
        removeFromCart={removeFromCart}
        cartLoading={cartLoading}
        hasDatasets={hasDatasets}
        disabled={!isLocationAvailable || disabled}
        showDropdown={showDownloadButton}
      />
    );
  };

  const renderPrice = (price: number, isFree: boolean) => {
    if (priceLoading)
      return <Spin indicator={<LoadingOutlined spin />} size='small' />;

    return isFree ? 'Free' : priceFormatter(price);
  };

  return (
    <Col span={12} className={styles.priceRegion}>
      {hasDatasets && (
        <Row gutter={16} className={styles.rowHedder}>
          <Col span={8}>{isDTU && 'Time Step'}</Col>
          <Col span={4} className={styles.colPriceHeader}>
            Price[EUR]
          </Col>
        </Row>
      )}

      {hasDatasets && isDTU && (
        <Row gutter={16} justify='space-between' className={styles.rowBody}>
          <Col span={8} className={styles.colText}>
            <Select
              style={{ width: 80 }}
              onChange={(val) => setTimeStepInterval(val)}
              defaultValue='60'
              value={timeStepInterval}
              disabled={isEmpty(datasetId) || addedToCart}
              size='small'
              data-testid='PriceSection-Time_Step_Select'
            >
              <Select.Option value='10'>10 min</Select.Option>
              <Select.Option value='15'>15 min</Select.Option>
              <Select.Option value='30'>30 min</Select.Option>
              <Select.Option value='60'>1 hour</Select.Option>
            </Select>
          </Col>
          <Col span={4} className={styles.colPrice}>
            {renderPrice(price, price === 0 && isTnw)}
          </Col>
          <Col span={12} className={styles.buttonRow}>
            {renderActionButtons({
              showDownloadButton,
              showCancelButton: showCancelDownloadButtonParameters,
              downloadType: 'parameters',
              addedToCart,
              cartLoading: parameterCartLoading,
              removeFromCart: handleRemoveFromCart,
              selectedAction: priceParameterSelection,
              setSelectedAction: setPriceParameterSelection,
            })}
          </Col>
        </Row>
      )}

      {/* Parameters */}
      {hasDatasets && !isDTU && (
        <Row gutter={16} justify='space-between' className={styles.rowBody}>
          <Col span={8} className={styles.colText}>
            Parameters
          </Col>
          <Col span={4} className={styles.colPrice}>
            {renderPrice(price, price === 0 && isTnw)}
          </Col>
          <Col span={12} className={styles.buttonRow}>
            {renderActionButtons({
              showDownloadButton,
              showCancelButton: showCancelDownloadButtonParameters,
              downloadType: 'parameters',
              addedToCart,
              cartLoading: parameterCartLoading,
              removeFromCart: handleRemoveFromCart,
              selectedAction: priceParameterSelection,
              setSelectedAction: setPriceParameterSelection,
            })}
          </Col>
        </Row>
      )}

      {/* 2D Spectra */}
      {hasDatasets && namePanel === DownloadCheckboxKey.SW && (
        <Row gutter={16} justify='space-between' className={styles.rowBody}>
          <Col
            span={8}
            className={show2DSpectra ? styles.colText : styles.disabledColText}
          >
            2D Spectra
          </Col>

          {show2DSpectra ? (
            <Col span={4} className={styles.colPrice}>
              {renderPrice(price, price === 0 && isTnw)}
            </Col>
          ) : (
            <Col span={4} className={styles.disabledColPrice}>
              -
            </Col>
          )}
          <Col span={12} className={styles.buttonRow}>
            {renderActionButtons({
              showDownloadButton: true,
              showCancelButton: showCancelDownloadButton2DSpectre,
              downloadType: '2DSpectra',
              addedToCart: spectraAddedToCart,
              cartLoading: spectraCartLoading,
              removeFromCart: handleRemoveSpectraFromCart,
              disabled: isSpectraButtonDisabled(),
              authenticatedCheck: true,
              selectedAction: priceSpectraSelection,
              setSelectedAction: setPriceSpectraSelection,
              addToCartSpectra: handleAddSpectraToCart,
            })}
          </Col>
        </Row>
      )}
    </Col>
  );
};
